<script setup>
// Two-step check-in: verify location first, then complete a secondary
// challenge (PIN / QR / Quiz). Both must pass. GPS + QR is handled at the
// CheckInSheet level (full-screen takeover); this component covers
// gps_and_pin and gps_and_quiz in the sheet, and shows the GPS step for
// gps_and_qr before the takeover kicks in.
// Port of `GpsAndBody` in the source `_shared/CheckInSheet.tsx`.
import { computed, ref } from 'vue'
import { secondaryLabel } from './checkin'
import StepIndicator from './StepIndicator.vue'
import GpsBody from './GpsBody.vue'
import PinBody from './PinBody.vue'
import QuizBody from './QuizBody.vue'

const props = defineProps({
  spec: { type: Object, required: true },
  gpsPassed: { type: Boolean, default: false },
  // Debug hook threaded to the GPS step (see GpsBody).
  forceState: { type: String, default: null },
  // Program-config recovery copy override, threaded to the GPS step.
  recoveryCopy: { type: Object, default: null },
  // Live validation handler. Step 1 runs validate-only (location checked
  // server-side, nothing executed); the secondary step submits BOTH factors
  // together — the server re-validates GPS + PIN/quiz as one entry.
  submit: { type: Function, default: null },
})

const emit = defineEmits(['success', 'gps-passed', 'help'])

// Coords that passed step 1, folded into the combined step-2 submission.
const passedCoords = ref(null)

const gpsSubmit = computed(() => {
  if (!props.submit) return null
  const submit = props.submit
  return (params) => submit({ ...params, validate: true })
})

const secondarySubmit = computed(() => {
  if (!props.submit) return null
  const submit = props.submit
  return (params) => submit({ ...(passedCoords.value || {}), ...params })
})

const onGpsSuccess = (coords) => {
  passedCoords.value = coords || null
  emit('gps-passed', coords || null)
}

// "Use venue PIN instead" substitutes the GPS step: no coords — the entered
// PIN alone reaches the server, where the staff-override rescue applies.
const onUsePin = () => {
  passedCoords.value = null
  emit('gps-passed', null)
}

const gpsSpec = computed(() => ({
  method: 'gps',
  venueName: props.spec.venueName,
  radiusLabel: props.spec.radiusLabel,
  simulatedProximity: props.spec.simulatedProximity,
  realGps: props.spec.realGps,
  // Carrying the pin lets GPS-failure recovery offer "Use venue PIN instead";
  // that substitutes the GPS step (emits gps-passed) and moves to step 2.
  pin: props.spec.pin,
  details: props.spec.details,
}))

const pinSpec = computed(() => ({
  method: 'pin',
  venueName: props.spec.venueName,
  pin: props.spec.pin,
  pinLength: props.spec.pinLength,
  hint: props.spec.hint,
  details: props.spec.details,
}))

const quizSpec = computed(() => ({
  method: 'quiz',
  venueName: props.spec.venueName,
  question: props.spec.question,
  choices: props.spec.choices,
  correctIndex: props.spec.correctIndex,
  maxAttempts: props.spec.maxAttempts,
  cooldownSec: props.spec.cooldownSec,
  lockoutMin: props.spec.lockoutMin,
  details: props.spec.details,
}))
</script>

<template>
  <!-- Step 1 — GPS -->
  <div v-if="!gpsPassed" class="cs-gps-and">
    <StepIndicator :step="1" :secondary="secondaryLabel(spec.method)" />
    <!-- "Use venue PIN instead" substitutes the GPS step → straight to step 2. -->
    <GpsBody
      :spec="gpsSpec"
      :force-state="forceState"
      :recovery-copy="recoveryCopy"
      :submit="gpsSubmit"
      @success="onGpsSuccess"
      @use-pin="onUsePin"
      @help="$emit('help')"
    />
  </div>

  <!-- Step 2 — secondary challenge -->
  <div v-else-if="spec.method === 'gps_and_pin'" class="cs-gps-and">
    <StepIndicator :step="2" secondary="PIN" />
    <PinBody :spec="pinSpec" :submit="secondarySubmit" @success="$emit('success')" />
  </div>

  <div v-else-if="spec.method === 'gps_and_quiz'" class="cs-gps-and">
    <StepIndicator :step="2" secondary="Quiz" />
    <QuizBody :spec="quizSpec" :submit="secondarySubmit" @success="$emit('success')" @help="$emit('help')" />
  </div>

  <!-- gps_and_qr is handled by CheckInSheet full-screen — shouldn't hit here. -->
</template>

<style lang="scss" scoped>
// Source: `space-y-3`
.cs-gps-and > * + * {
  margin-top: 12px; // space-y-3
}
</style>
